import type { ScopeRef } from "@enduring-systems/entitlements";
import { scopeKey } from "@enduring-systems/entitlements";
import { useCallback, useEffect, useState } from "react";

import { type AuditLogEntry, readAuditLog } from "./entitlements-api";
import { READ_DEBOUNCE_MS } from "./use-effective";

/** Rows per request. Small enough to render, large enough to answer "what changed". */
export const AUDIT_PAGE_SIZE = 25;

export interface UseAuditLogReturn {
  readonly entries: readonly AuditLogEntry[];
  readonly isLoading: boolean;
  readonly error: string | null;
  /** Whether the service is still holding rows past the ones loaded. */
  readonly hasMore: boolean;
  /** Rows the service could not render, accumulated across the pages loaded. */
  readonly unreadable: number;
  loadMore(): void;
}

/** Which page of which log; a result for any other one is not an answer. */
interface PageRequest {
  readonly selection: string;
  /** null asks for the first page; anything else continues from that cursor. */
  readonly cursor: string | null;
  /**
   * Identifies THIS request, not merely which selection it belongs to.
   *
   * The selection alone is not enough. Leave a selection with a page in flight
   * and come back to it (toggle the narrowing on, then off) and the late
   * response finds the selection it named restored, so a selection-only guard
   * accepts it: a stale CONTINUATION then appends page two onto a freshly reset
   * page one, and the panel shows a history missing its first page while looking
   * complete. Monotonic, so only the answer to the request still in flight is
   * ever applied.
   */
  readonly id: number;
}

interface LogState {
  readonly selection: string;
  /** The request this state answers; see `PageRequest.id`. */
  readonly requestId: number;
  readonly entries: readonly AuditLogEntry[];
  readonly nextCursor: string | null;
  readonly unreadable: number;
  readonly isLoading: boolean;
  readonly error: string | null;
}

function selectionOf(
  scope: ScopeRef | null,
  capabilityId: string | null,
  reloadKey: number
): string {
  return `${scope === null ? "none" : scopeKey(scope)}|${capabilityId ?? "all"}|${reloadKey}`;
}

const EMPTY: readonly AuditLogEntry[] = [];

/**
 * The mutation history for the selected scope, newest first, accumulated a page at
 * a time. `capabilityId` narrows it; null reads every change at the scope.
 *
 * Not the same log as `src/audit-log`, which reads identity's Postgres
 * `audit_events` for an enterprise (org-management actions, keyed by
 * enterprise/org-unit). This one is the entitlements grant audit in DynamoDB,
 * keyed by entitlement SCOPE, and the two share neither a store nor a shape.
 *
 * Every result is stored WITH the selection it answers and discarded if the
 * selection has moved on, the same rule `useEffective` follows: this is an audited
 * cross-tenant read, and rows attributed to the wrong tenant are worse than no rows.
 * A change of selection also drops the accumulated pages, because they describe a
 * log nobody is looking at any more.
 *
 * The first page waits out the scope-id typing (the id is free text, and an
 * intermediate prefix can name a real tenant); a continuation does not, since it
 * comes from a click.
 *
 * `hasMore` follows the CURSOR, not the size of the last page. A narrowed page is
 * filtered after it is read, so it can arrive short or even empty with rows still
 * behind it, and stopping on a short page would silently truncate the history.
 *
 * `scope` must be referentially stable across renders (the page memoizes it).
 */
export function useAuditLog(
  scope: ScopeRef | null,
  capabilityId: string | null,
  reloadKey: number
): UseAuditLogReturn {
  const selection = selectionOf(scope, capabilityId, reloadKey);
  const [request, setRequest] = useState<PageRequest>({ selection, cursor: null, id: 0 });
  const [state, setState] = useState<LogState>({
    selection,
    requestId: 0,
    entries: EMPTY,
    nextCursor: null,
    unreadable: 0,
    // A scope was already chosen on the first render, so a read is coming and the
    // effect that starts it has not run yet. Starting at `false` would render one
    // frame of "no changes have been recorded at this scope", which is a claim
    // about a log nobody has read.
    isLoading: scope !== null,
    error: null,
  });

  useEffect(() => {
    setRequest((previous) =>
      previous.selection === selection ? previous : { selection, cursor: null, id: previous.id + 1 }
    );
  }, [selection]);

  useEffect(() => {
    // The reset above lands in its own render, so skip the pass where the request
    // still names the previous selection rather than reading the old log again.
    if (scope === null || request.selection !== selection) {
      if (scope === null) {
        setState({
          selection,
          requestId: request.id,
          entries: EMPTY,
          nextCursor: null,
          unreadable: 0,
          isLoading: false,
          error: null,
        });
      }
      return;
    }

    const continuing = request.cursor !== null;
    const requestId = request.id;
    setState((previous) => {
      const kept = continuing && previous.selection === selection;
      return {
        selection,
        requestId,
        entries: kept ? previous.entries : EMPTY,
        // The cursor being followed is held, not cleared. Clearing it here left
        // a FAILED continuation with `hasMore` permanently false, which hid the
        // button and made the rest of the history unreachable without changing
        // scope: the one moment the operator most needs to retry.
        nextCursor: kept ? previous.nextCursor : null,
        unreadable: kept ? previous.unreadable : 0,
        isLoading: true,
        error: null,
      };
    });

    const timer = setTimeout(
      () => {
        readAuditLog({
          scope,
          capabilityId,
          limit: AUDIT_PAGE_SIZE,
          cursor: request.cursor,
        })
          .then((page) => {
            setState((previous) =>
              previous.requestId !== requestId
                ? previous
                : {
                    selection,
                    requestId,
                    entries: continuing ? [...previous.entries, ...page.entries] : page.entries,
                    nextCursor: page.nextCursor,
                    unreadable: (continuing ? previous.unreadable : 0) + (page.unreadable ?? 0),
                    isLoading: false,
                    error: null,
                  }
            );
          })
          .catch((e: unknown) => {
            setState((previous) =>
              previous.requestId !== requestId
                ? previous
                : {
                    ...previous,
                    isLoading: false,
                    error: e instanceof Error ? e.message : "Failed to read the audit log",
                  }
            );
          });
      },
      continuing ? 0 : READ_DEBOUNCE_MS
    );

    return () => clearTimeout(timer);
  }, [scope, capabilityId, selection, request]);

  const current = state.selection === selection;
  const nextCursor = current ? state.nextCursor : null;
  const isLoading = current ? state.isLoading : scope !== null;

  const loadMore = useCallback(() => {
    // A new request id every time, deliberately: asking for the SAME cursor
    // again is what a retry after a failed continuation is, so treating a
    // repeat as a duplicate would make the retry a no-op. Double-appending is
    // prevented by the guards instead: a click while a page is in flight
    // returns here, and two clicks in one tick batch into a single request.
    if (nextCursor === null || isLoading) return;
    setRequest((previous) => ({ selection, cursor: nextCursor, id: previous.id + 1 }));
  }, [selection, nextCursor, isLoading]);

  return {
    entries: current ? state.entries : EMPTY,
    isLoading,
    error: current ? state.error : null,
    hasMore: nextCursor !== null,
    unreadable: current ? state.unreadable : 0,
    loadMore,
  };
}
